import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useCallback} from 'react';
import {useNavigate} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {StyleSheet, View, Text, Pressable} from 'react-native';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';
import {selectors} from 'store/slices/files';

import * as filesEffects from 'interface/hooks/effects/files';

export const filesPeekHeaderHeight = 42;
export const filesPeekHeaderHeightPicker = 52;

export interface FolderNavigatorProps {
  id: string;
  picker?: boolean;
}

export function FolderNavigator(props: FolderNavigatorProps) {
  const {t} = useLingui();
  const folder = useSelector(selectors.getItem(props.id));
  const pick = useSelector(selectors.getPick);
  const hierarchy = folder && folder.hierarchy.length > 0 && folder.hierarchy[folder.hierarchy.length - 2];
  const parent = hierarchy && hierarchy[0] !== props.id ? hierarchy[0] : 'myfiles';
  const depth = folder ? folder.hierarchy && folder.hierarchy.length : 0;
  const isRoot = !folder || folder.name === 'myfiles';
  const title = folder && !isRoot ? folder.name : props.picker ? t`My Files` : t`My Folders`;
  const classes = {
    root: [
      styles.root,
      props.picker && styles.picker,
    ],
    title: [
      styles.title,
      props.picker && styles.titlePicker,
      isRoot && styles.titleRoot,
      isRoot && props.picker && styles.titleRootPicker,
    ],
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gotoFolder = useCallback(() => navigate(`/folder/${props.id}`), [props.id]);
  const exit = useCallback(filesEffects.pick(dispatch, null), []);
  const goBackPick = useCallback(filesEffects.pick(dispatch, {...pick, destination: parent}), [pick]);

  return (
    <View style={classes.root}>
      {depth > 0 &&
        <View style={styles.back}>
          <Pressable style={styles.back} onPress={goBackPick}>
            <Svg viewBox="0 0 24 24" width={props.picker ? 24 : 18} height={props.picker ? 24 : 18}>
              <Path d={vectors.icons.back} fill={colors.neutral.mid} fillOpacity={0.75}/>
            </Svg>
          </Pressable>
        </View>
      }
      {!props.picker &&
        <View style={styles.button}>
          <Pressable onPress={props.picker ? undefined : gotoFolder}>
            {depth === 0 &&
              <Svg style={styles.folderIcon} viewBox={'0 0 24 24'} width={20} height={20}>
                <Path d={vectors.icons.folder} fill={colors.neutral.mid} fillOpacity={0.5}/>
              </Svg>
            }
            <Text
              style={classes.title}
              numberOfLines={1}
              ellipsizeMode="middle"
              textBreakStrategy="balanced">
              {title}
            </Text>
          </Pressable>
        </View>
      }
      {props.picker &&
        <View style={styles.button}>
          <Text
            style={classes.title}
            numberOfLines={1}
            ellipsizeMode="middle"
            textBreakStrategy="balanced">
            {title}
          </Text>
          <Pressable onPress={exit}>
            <View style={styles.close}>
                <Svg viewBox="0 0 14 14" width={14} height={14}>
                  <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
                </Svg>
            </View>
          </Pressable>
        </View>
      }
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.files.navigator.border,
    backgroundColor: theme.files.navigator.fill,
    height: filesPeekHeaderHeight,
  },
  picker: {
    height: filesPeekHeaderHeightPicker,
  },
  back: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    margin: 2,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 0,
    margin: 0,
  },
  folderIcon: {
    marginTop: 6,
    marginLeft: 10,
  },
  title: {
    flex: 1,
    fontSize: 12,
    color: theme.files.navigator.text,
  },
  titlePicker: {
    fontSize: 14,
  },
  titleRoot: {
    marginLeft: 6,
  },
  titleRootPicker: {
    marginLeft: 16,
  },
  close: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 46,
    height: 46,
  },
});
